import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl/dist/maplibre-gl-csp';
import { Protocol } from 'pmtiles';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Link, useSearchParams } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon, ClipboardDocumentCheckIcon, ClockIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import Modal from '../components/Modal';

// Survey timer text, e.g. "4m 05s".
const elapsed = (from) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(from).getTime()) / 1000));
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};

// The reactive, whole-England pole map — the desktop survey / planning view.
//
// All ~4.3m poles live in our own mirror; the map loads the ones in the current
// viewport and clusters them, so dense areas read as a count that bursts into
// individual poles as you zoom. Below a working zoom it asks you to zoom in
// rather than trying to draw a county at once. Search flies to a place; a pole
// opens a full breakdown; and any pole can measure a straight line to a point.
maplibregl.setWorkerUrl('/maplibre-gl-csp-worker.js');
const protocol = new Protocol();
maplibregl.addProtocol('pmtiles', protocol.tilev4);

const TILES = '/tiles/uk.pmtiles';
const LOAD_ZOOM = 11;
const VERDICT_COLOUR = { hoist: '#dc2626', caution: '#d97706', climb: '#059669' };
const BRAND = '#711C91';

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));
const verdictLabel = (v) => ({ hoist: 'Hoist required', caution: 'Assess on site', climb: 'Climbable' }[v] || v);
// Why a pole survey manager overrides the register's call (server: services/poleOverrides).
const OVERRIDE_REASONS = {
  new_pole: 'New pole',
  retested: 'Retested (D-plate date seen)',
  hollow: 'Hollow pole',
  failed_preclimb: 'Failed pre-climb',
  other: 'Other',
};
const ukDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : null);

function metresBetween([lon1, lat1], [lon2, lat2]) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1); const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

const style = {
  version: 8,
  // Self-hosted Noto Sans glyphs, so symbol layers (house numbers, road names,
  // pole IDs — added in Phase 2) can render text. The fontstack name has spaces;
  // MapLibre URL-encodes it into this template.
  glyphs: `${window.location.origin}/fonts/{fontstack}/{range}.pbf`,
  sources: { basemap: { type: 'vector', url: `pmtiles://${window.location.origin}${TILES}`, attribution: 'OpenStreetMap · Protomaps · Openreach' } },
  layers: [
    { id: 'bg', type: 'background', paint: { 'background-color': '#b9cbd9' } },
    { id: 'earth', type: 'fill', source: 'basemap', 'source-layer': 'earth', paint: { 'fill-color': '#f3f0ea' } },
    {
      id: 'landuse', type: 'fill', source: 'basemap', 'source-layer': 'landuse',
      filter: ['in', ['get', 'kind'], ['literal', ['park', 'forest', 'wood', 'nature_reserve', 'grass', 'farmland', 'scrub']]],
      paint: { 'fill-color': '#d8e3cb' },
    },
    { id: 'water', type: 'fill', source: 'basemap', 'source-layer': 'water', paint: { 'fill-color': '#aecbe0' } },
    { id: 'buildings', type: 'fill', source: 'basemap', 'source-layer': 'buildings', minzoom: 13, paint: { 'fill-color': '#e6e0d6', 'fill-outline-color': '#d2cabc' } },
    // Roads drawn as casing + white fill so they read like a street map rather than
    // faint scratches. Minor first, then major on top so junctions look right.
    {
      id: 'roads-minor-casing', type: 'line', source: 'basemap', 'source-layer': 'roads', minzoom: 12,
      filter: ['in', ['get', 'kind'], ['literal', ['minor_road', 'medium_road']]],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#cdc4b6', 'line-width': ['interpolate', ['linear'], ['zoom'], 12, 1.2, 16, 7] },
    },
    {
      id: 'roads-minor', type: 'line', source: 'basemap', 'source-layer': 'roads', minzoom: 12,
      filter: ['in', ['get', 'kind'], ['literal', ['minor_road', 'medium_road']]],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#ffffff', 'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.4, 16, 5] },
    },
    {
      id: 'roads-major-casing', type: 'line', source: 'basemap', 'source-layer': 'roads',
      filter: ['in', ['get', 'kind'], ['literal', ['highway', 'major_road']]],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#c9a98f', 'line-width': ['interpolate', ['linear'], ['zoom'], 5, 1.2, 10, 4, 16, 11] },
    },
    {
      id: 'roads-major', type: 'line', source: 'basemap', 'source-layer': 'roads',
      filter: ['in', ['get', 'kind'], ['literal', ['highway', 'major_road']]],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#ffffff', 'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.6, 10, 2.4, 16, 8] },
    },
    { id: 'boundaries', type: 'line', source: 'basemap', 'source-layer': 'boundaries', paint: { 'line-color': '#b3a9ba', 'line-dasharray': [2, 2], 'line-width': 0.9 } },
    // --- text labels (need the glyph server from Phase 1) ---
    {
      id: 'road-labels',
      type: 'symbol',
      source: 'basemap',
      'source-layer': 'roads',
      minzoom: 13,
      filter: ['has', 'name'],
      layout: {
        'symbol-placement': 'line',
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 11,
      },
      paint: { 'text-color': '#5b5560', 'text-halo-color': '#ffffff', 'text-halo-width': 1.4 },
    },
    {
      id: 'place-labels',
      type: 'symbol',
      source: 'basemap',
      'source-layer': 'places',
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 6, 11, 12, 16],
      },
      paint: { 'text-color': '#4a4551', 'text-halo-color': '#ffffff', 'text-halo-width': 1.4 },
    },
  ],
};

// Little teardrop pin for the legend — matches the support-request markers.
function PinGlyph({ color }) {
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" aria-hidden="true">
      <path d="M5 13.5C5 13.5 9 7.6 9 4.6A4 4 0 1 0 1 4.6C1 7.6 5 13.5 5 13.5Z" fill={color} stroke="#fff" strokeWidth="1" />
      <circle cx="5" cy="4.6" r="1.5" fill="#fff" />
    </svg>
  );
}

function Field({ label, children }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-gray-800">{children}</dd>
    </div>
  );
}

export default function PoleMap() {
  const holder = useRef(null);
  const map = useRef(null);
  const measureFrom = useRef(null);
  const houseMarker = useRef(null);
  const distPopup = useRef(null);
  const treeMarker = useRef(null);
  const [status, setStatus] = useState('Zoom in to load poles.');
  const [hint, setHint] = useState('');
  // Tile-load progress, driven off MapLibre's data events.
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [houseNote, setHouseNote] = useState('');
  const tilesReq = useRef(0);
  const tilesDone = useRef(0);
  const [pc, setPc] = useState('');
  const [poleQ, setPoleQ] = useState({ exchange: '', dp: '', pole: '' });
  const [addr, setAddr] = useState('');
  // Support-requests overlay (MEWP/specialist/supervisor, mirrored from SharePoint).
  const [showSupport, setShowSupport] = useState(false);
  const showSupportRef = useRef(false);
  const loadSupportRef = useRef(() => {});
  // Satellite (aerial) basemap — for eyeballing trees/obstructions at the pole.
  const [showSatellite, setShowSatellite] = useState(false);
  const [addrList, setAddrList] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [mode, setMode] = useState('address');
  const [selected, setSelected] = useState(null); // the clicked pole's detail
  // Manual override form in the pole panel (pole survey managers only).
  const [ovrForm, setOvrForm] = useState(null); // { verdict, reason, note } while open
  const [ovrBusy, setOvrBusy] = useState(false);
  const [ovrError, setOvrError] = useState('');

  // Survey audit trail (TKT-00042): the address picked in the search, the
  // survey in progress (resumed after a reload), and the finish dialog. The map
  // handlers are bound once, so they read the survey through a ref.
  const [place, setPlace] = useState(null);
  const [survey, setSurvey] = useState(null);
  const surveyRef = useRef(null);
  const [surveyMeta, setSurveyMeta] = useState({ outcomes: {}, can_view_log: false });
  const [finishing, setFinishing] = useState(false);
  const [outcome, setOutcome] = useState('');
  const [surveyNotes, setSurveyNotes] = useState('');
  // Why the hoist is needed (21/10/2026): required for a hoist outcome,
  // pre-ticked from the survey's evidence when the dialog opens.
  const [mewpPicks, setMewpPicks] = useState([]);
  const [surveyError, setSurveyError] = useState('');
  const [, setTick] = useState(0);
  // Which pole serves the picked address (08/10/2026), and which houses the
  // clicked pole feeds.
  const [serving, setServing] = useState(null);
  const [servedBy, setServedBy] = useState(null);
  useEffect(() => {
    setServing(null);
    if (!place) return undefined;
    let gone = false;
    api.get('/api/poles/serving', { params: { id: place.id, lat: place.lat, lon: place.lon } })
      .then(({ data }) => { if (!gone) setServing(data); })
      .catch(() => {});
    return () => { gone = true; };
  }, [place]);
  useEffect(() => {
    const m = map.current;
    const src = m?.getSource('serving');
    if (!src) return;
    const prem = serving?.premise;
    const pole = serving?.pole;
    if (!prem || !pole || pole.latitude == null) { src.setData({ type: 'FeatureCollection', features: [] }); return; }
    const house = [prem.longitude, prem.latitude];
    const at = [pole.longitude, pole.latitude];
    src.setData({
      type: 'FeatureCollection',
      features: [
        { type: 'Feature', geometry: { type: 'LineString', coordinates: [at, house] }, properties: {} },
        { type: 'Feature', geometry: { type: 'Point', coordinates: at }, properties: { role: 'pole' } },
        { type: 'Feature', geometry: { type: 'Point', coordinates: house }, properties: { role: 'house' } },
      ],
    });
    m.fitBounds([[Math.min(house[0], at[0]), Math.min(house[1], at[1])], [Math.max(house[0], at[0]), Math.max(house[1], at[1])]],
      { padding: 140, maxZoom: 18, duration: 700 });
  }, [serving]);
  useEffect(() => {
    setServedBy(null);
    const g = selected?.grid_ref;
    if (!g) return undefined;
    let gone = false;
    api.get('/api/poles/served-by', { params: { grid_ref: g } })
      .then(({ data }) => { if (!gone) setServedBy(data.premises || []); })
      .catch(() => {});
    return () => { gone = true; };
  }, [selected?.grid_ref]);
  const openServingPole = () => {
    const pole = serving?.pole;
    if (!pole) return;
    setSelected({ ...pole, verdictColour: VERDICT_COLOUR[pole.verdict] });
    recordEvidence('pole', {
      exchange: pole.exchange, dp: pole.dp, pole: pole.pole, grid_ref: pole.grid_ref, verdict: pole.verdict,
      result: pole.result_description || pole.result, result_code: pole.result || null, out_of_test: pole.out_of_test, inspected: pole.inspected, cbt: pole.cbt || null,
    });
    const len = Number(pole.length_m);
    measureFrom.current = {
      coords: [pole.longitude, pole.latitude], label: `DP ${pole.dp}/${pole.pole}`,
      poleTopM: Number.isFinite(len) && len > 0 ? Math.max(3, len - 1.7) : null,
    };
    setHint('Now click the house to measure the drop and check for trees.');
  };
  useEffect(() => { surveyRef.current = survey; }, [survey]);
  useEffect(() => {
    api.get('/api/poles/surveys/meta').then(({ data }) => setSurveyMeta(data)).catch(() => {});
    // Opened from Climb Check, the job's own effect below decides what to do
    // with an open survey.
    if (!ccId) api.get('/api/poles/surveys/mine/open').then(({ data }) => { if (data.survey) setSurvey(data.survey); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One survey at a time per person (Ruan, 19/10/2026). A second tab never
  // cancels the open survey on its own: it offers "go back to it" or "cancel it
  // and survey here". Tabs tell each other when a survey ends, and a tab
  // re-checks when it is looked at again, so nobody works on a dead survey.
  const [blocked, setBlocked] = useState(null); // the survey open elsewhere
  const blockedRef = useRef(null);
  useEffect(() => { blockedRef.current = blocked; }, [blocked]);
  const [surveyNote, setSurveyNote] = useState('');
  // This tab's own messages come back to it too: tag them and ignore our own.
  const tabId = useRef(Math.random().toString(36).slice(2)).current;
  const tell = (msg) => { try { const bc = new BroadcastChannel('pole-survey'); bc.postMessage({ ...msg, from: tabId }); bc.close(); } catch { /* old browser */ } };
  useEffect(() => {
    let bc = null;
    try {
      bc = new BroadcastChannel('pole-survey');
      bc.onmessage = ({ data: m }) => {
        if (!m || !m.id || m.from === tabId) return;
        if (surveyRef.current?.id === m.id) {
          setSurvey(null); setFinishing(false);
          setSurveyNote(m.kind === 'cancelled' ? `This survey was cancelled: you started ${m.next || 'another one'}.` : 'This survey was saved in another tab.');
        }
        if (blockedRef.current?.id === m.id) {
          setBlocked(null);
          setSurveyNote('That survey has ended. Start survey when you are ready.');
        }
      };
    } catch { /* old browser */ }
    const onShow = () => {
      if (document.visibilityState !== 'visible') return;
      api.get('/api/poles/surveys/mine/open').then(({ data }) => {
        const open = data.survey;
        if (surveyRef.current && open?.id !== surveyRef.current.id) {
          setSurvey(null); setFinishing(false);
          setSurveyNote('This survey is no longer open: it was saved or cancelled in another tab.');
        }
        if (blockedRef.current && open?.id !== blockedRef.current.id) setBlocked(null);
      }).catch(() => {});
    };
    document.addEventListener('visibilitychange', onShow);
    return () => { bc?.close(); document.removeEventListener('visibilitychange', onShow); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Go back to the open survey: close this tab (browsers allow it for a tab
  // opened from a link with no history), else say so.
  const leaveForOpen = () => {
    window.close();
    window.setTimeout(() => setSurveyNote('Close this tab and go back to the tab with your open survey.'), 300);
  };

  // Opened from Climb Check (?cc=<job id>, 19/10/2026): the job's house is the
  // address to survey and its CBT pole is opened ready to measure the drop. A
  // survey started here is tied to the job, so its outcome goes back onto it.
  const [params] = useSearchParams();
  const ccId = Number(params.get('cc')) || null;
  const [ccJob, setCcJob] = useState(null);
  const [ccNote, setCcNote] = useState('');
  const ccMarker = useRef(null);
  useEffect(() => {
    if (!ccId) return undefined;
    let gone = false;
    (async () => {
      let job;
      try { ({ data: job } = await api.get(`/api/climb-check/job/${ccId}`)); } catch (e) {
        if (!gone) setStatus(e?.response?.data?.error || 'Could not open the Climb Check job.');
        return;
      }
      if (gone) return;
      setCcJob(job);
      const prem = job.premise;
      const at = prem ? [prem.longitude, prem.latitude] : job.odin ? [job.odin.longitude, job.odin.latitude] : null;
      setPlace({ id: prem?.id || null, label: prem?.label || job.address, lat: at ? at[1] : null, lon: at ? at[0] : null, postcode: prem?.postcode || null });
      setAddr(prem?.label || job.address);
      // The survey starts on its own (Ruan, 19/10/2026), unless one is already
      // running: this job's is resumed, another address's is left alone (starting
      // would cancel it). A job that already has a survey just opens.
      try {
        const { data: open } = await api.get('/api/poles/surveys/mine/open');
        if (gone) return;
        if (open.survey && open.survey.climb_check_job_id === job.id) {
          setSurvey(open.survey); surveyRef.current = open.survey;
        } else if (open.survey) {
          setBlocked(open.survey);
        } else if (!job.survey) {
          const { data: s } = await api.post('/api/poles/surveys', {
            address: prem?.label || job.address, postcode: prem?.postcode || null,
            latitude: at ? at[1] : null, longitude: at ? at[0] : null, climb_check_job_id: job.id,
          });
          if (gone) return;
          setSurvey(s); surveyRef.current = s;
        } else {
          setCcNote('This job already has a survey: start another to replace it.');
        }
      } catch (e) {
        if (!gone) setCcNote(e?.response?.data?.error || 'Could not start the survey: use Start survey.');
      }
      if (job.odin && map.current) {
        // ODIN's CBT position: where the pole is when Openreach doesn't hold it.
        ccMarker.current?.remove();
        ccMarker.current = new maplibregl.Marker({ color: '#3e4784' })
          .setLngLat([job.odin.longitude, job.odin.latitude])
          .setPopup(new maplibregl.Popup({ offset: 24 }).setText(`CBT L4 ${job.l4_closure} (mapped position)`))
          .addTo(map.current);
      }
      if (job.cbt) {
        try {
          const { data } = await api.get('/api/poles/by-pole', { params: job.cbt });
          const hit = (data.poles || [])[0];
          if (!gone && hit && hit.latitude != null) {
            setSelected({ ...hit, verdictColour: VERDICT_COLOUR[hit.verdict] });
            recordEvidence('pole', {
              exchange: hit.exchange, dp: hit.dp, pole: hit.pole, grid_ref: hit.grid_ref, verdict: hit.verdict,
              result: hit.result_description || hit.result, result_code: hit.result || null, out_of_test: hit.out_of_test, inspected: hit.inspected, cbt: hit.cbt || null,
            });
            const len = Number(hit.length_m);
            measureFrom.current = {
              coords: [hit.longitude, hit.latitude], label: `DP ${hit.dp}/${hit.pole}`,
              poleTopM: Number.isFinite(len) && len > 0 ? Math.max(3, len - 1.7) : null,
            };
            // Frame the CBT pole and the house together. Don't wait on the
            // serving-pole line: it only draws when the house's grid ref matches a
            // register pole exactly (16 Back Lane, Huthwaite didn't, so the map
            // never moved).
            const pole = [Number(hit.longitude), Number(hit.latitude)];
            if (prem) {
              const house = [prem.longitude, prem.latitude];
              map.current?.fitBounds(
                [[Math.min(house[0], pole[0]), Math.min(house[1], pole[1])], [Math.max(house[0], pole[0]), Math.max(house[1], pole[1])]],
                { padding: 140, maxZoom: 18, duration: 800 });
            } else map.current?.flyTo({ center: pole, zoom: 18, duration: 800 });
            setHint('CBT pole open: click the house to measure the drop and check for trees.');
            return;
          }
        } catch { /* fall through to the house or ODIN position */ }
      }
      if (at) map.current?.flyTo({ center: at, zoom: 18, duration: 800 });
      setHint(job.odin ? "No Openreach pole at this CBT: the purple pin is the CBT's mapped position." : 'No CBT pole on record: find it on the map.');
    })();
    return () => { gone = true; ccMarker.current?.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccId]);
  // Only a survey of the job's own address is tied to it.
  const ccLinked = ccJob && place && (place.label === (ccJob.premise?.label || ccJob.address)) ? ccJob : null;
  useEffect(() => {
    if (!survey) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [survey]);
  // Evidence: fire-and-forget, never in the surveyor's way.
  const recordEvidence = (kind, detail) => {
    const s = surveyRef.current;
    if (!s) return;
    api.post(`/api/poles/surveys/${s.id}/events`, { kind, detail }).catch(() => {});
  };
  // replace: the survey open elsewhere that the surveyor chose to cancel for this one.
  const startSurvey = async (replace = null) => {
    setSurveyError(''); setSurveyNote('');
    try {
      if (!replace) {
        // Open in another tab? Never cancel it silently: offer the choice.
        const { data: o } = await api.get('/api/poles/surveys/mine/open');
        if (o.survey) { setBlocked(o.survey); return; }
      } else {
        await api.post(`/api/poles/surveys/${replace.id}/cancel`).catch(() => {});
        tell({ kind: 'cancelled', id: replace.id, next: place.label });
        setBlocked(null);
      }
      const { data } = await api.post('/api/poles/surveys', {
        address: place.label, postcode: place.postcode || null, latitude: place.lat, longitude: place.lon,
        climb_check_job_id: ccLinked?.id || null,
      });
      setSurvey(data); surveyRef.current = data; setMewpPicks([]);
    } catch (e) { setSurveyError(e?.response?.data?.error || 'Could not start the survey.'); setSurveyNote(e?.response?.data?.error || 'Could not start the survey.'); }
  };
  const finishSurvey = async () => {
    setSurveyError('');
    try {
      const { data: done } = await api.post(`/api/poles/surveys/${survey.id}/finish`, {
        outcome, notes: surveyNotes, ...(outcome === 'hoist' ? { mewp_reasons: mewpPicks } : {}),
      });
      tell({ kind: 'finished', id: survey.id });
      setSurvey(null); setFinishing(false); setOutcome(''); setSurveyNotes(''); setMewpPicks([]);
      if (done?.climb_check_job_id) {
        // Tell an open Climb Check tab to reload; it also reloads when refocused.
        try { const bc = new BroadcastChannel('climb-check'); bc.postMessage({ job: done.climb_check_job_id }); bc.close(); } catch { /* old browser */ }
        setHint('Survey saved and sent back to Climb Check. You can close this tab.');
      } else setHint('Survey saved.');
    } catch (e) {
      if (e?.response?.status === 409) {
        // Already ended in another tab: don't leave the surveyor on a dead survey.
        setSurvey(null); setFinishing(false);
        setSurveyNote('This survey had already ended in another tab, so this outcome was not saved. Start survey to record it again.');
      } else setSurveyError(e?.response?.data?.error || 'Could not save the survey.');
    }
  };
  const openFinish = async () => {
    setSurveyError('');
    setFinishing(true);
    if (mewpPicks.length) return;
    try {
      const { data } = await api.get('/api/poles/surveys/mine/open');
      if (data.survey?.id === survey.id) setMewpPicks(data.survey.suggested_mewp_reasons || []);
    } catch { /* nothing pre-ticked */ }
  };
  const toggleMewp = (r) => setMewpPicks((p) => (p.includes(r) ? p.filter((x) => x !== r) : [...p, r]));
  const cancelSurvey = async () => {
    try { await api.post(`/api/poles/surveys/${survey.id}/cancel`); } catch { /* already gone */ }
    tell({ kind: 'cancelled', id: survey.id, next: null });
    setSurvey(null); setFinishing(false); setMewpPicks([]);
  };

  // Address type-ahead against the OSM address_points table (England-wide). A
  // full UK postcode won't match the address full-text index (its tokens are too
  // short), so when the box holds one we geocode it and offer it as the top
  // suggestion — the Address box then handles postcodes too, not just the tab.
  useEffect(() => {
    if (mode !== 'address' || addr.trim().length < 3) { setAddrList([]); return undefined; }
    let cancelled = false;
    const q = addr.trim();
    const isPostcode = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/.test(q);
    const t = setTimeout(async () => {
      const out = [];
      if (isPostcode) {
        try {
          const { data } = await api.get('/api/poles/geocode', { params: { postcode: q } });
          if (data && data.lat != null) out.push({ id: 'pc', label: `${data.postcode} (postcode)`, lat: data.lat, lon: data.lon, kind: 'postcode' });
        } catch { /* not a known postcode — fall through to address search */ }
      }
      try {
        const { data } = await api.get('/api/poles/address-suggest', { params: { q } });
        (data.results || []).forEach((r) => out.push(r));
      } catch { /* keep whatever the postcode lookup gave us */ }
      if (!cancelled) setAddrList(out);
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [addr, mode]);

  const clearMeasure = () => {
    const m = map.current;
    measureFrom.current = null;
    if (houseMarker.current) { houseMarker.current.remove(); houseMarker.current = null; }
    if (distPopup.current) { distPopup.current.remove(); distPopup.current = null; }
    if (treeMarker.current) { treeMarker.current.remove(); treeMarker.current = null; }
    m?.getSource('measure')?.setData({ type: 'FeatureCollection', features: [] });
    if (m?.getLayer('measure-line')) m.setPaintProperty('measure-line', 'line-color', '#111827');
    setHint('');
  };

  useEffect(() => {
    if (map.current || !holder.current) return undefined;
    const m = new maplibregl.Map({
      container: holder.current, style, center: [-2.4, 53.2], zoom: 6, attributionControl: false,
    });
    map.current = m;
    m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
    m.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');
    m.on('error', (e) => console.error('[pole-map]', (e && e.error && e.error.message) || e));

    let timer = null;
    const load = async () => {
      if (m.getZoom() < LOAD_ZOOM) {
        m.getSource('poles')?.setData({ type: 'FeatureCollection', features: [] });
        setStatus('Zoom in to load poles.');
        return;
      }
      const b = m.getBounds();
      const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(',');
      setStatus('Loading…');
      try {
        const { data } = await api.get('/api/poles/in-bounds', { params: { bbox, limit: 5000 } });
        m.getSource('poles')?.setData({
          type: 'FeatureCollection',
          features: (data.poles || []).map((p) => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
            properties: { ...p, verdictColour: VERDICT_COLOUR[p.verdict] || '#059669' },
          })),
        });
        setStatus(data.capped
          ? `Showing ${data.poles.length} of ${data.total} here — zoom in for the rest.`
          : `${data.total} pole${data.total === 1 ? '' : 's'} in view.`);
      } catch (e) { setStatus('Could not load poles for this area.'); }
    };
    // House numbers, from the OSM address layer, only when zoomed right in — they
    // are far denser than poles, so they would be noise at street level.
    // House numbers only very zoomed in: a dense city viewport holds >1,000
    // addresses, and that many text labels re-rendering on every pan is what
    // makes it feel buggy. At 17+ the viewport is small enough to stay smooth.
    const HOUSE_ZOOM = 17;
    const loadHouses = async () => {
      if (m.getZoom() < HOUSE_ZOOM) { m.getSource('houses')?.setData({ type: 'FeatureCollection', features: [] }); setHouseNote(''); return; }
      const b = m.getBounds();
      const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(',');
      try {
        const { data } = await api.get('/api/poles/houses', { params: { bbox } });
        const feats = (data.addresses || []).filter((a) => a.n);
        m.getSource('houses')?.setData({
          type: 'FeatureCollection',
          features: feats.map((a) => ({
            type: 'Feature', geometry: { type: 'Point', coordinates: [a.lon, a.lat] }, properties: { n: a.n },
          })),
        });
        // OSM address coverage is thin rurally — say so rather than leave a blank.
        setHouseNote(feats.length ? '' : 'No OSM house numbers mapped in this area.');
      } catch { /* leave what's there */ }
    };
    // Every property in view (OS Open UPRN), at the same zoom as the house
    // numbers: a dot per property even where no address is known.
    const loadProperties = async () => {
      if (m.getZoom() < HOUSE_ZOOM) { m.getSource('properties')?.setData({ type: 'FeatureCollection', features: [] }); return; }
      const b = m.getBounds();
      const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(',');
      try {
        const { data } = await api.get('/api/poles/properties', { params: { bbox } });
        m.getSource('properties')?.setData({
          type: 'FeatureCollection',
          features: (data.properties || []).map((p) => ({
            type: 'Feature', geometry: { type: 'Point', coordinates: [p.lon, p.lat] }, properties: { uprn: p.uprn },
          })),
        });
      } catch { /* leave what's there */ }
    };
    // Recent support requests (MEWP etc.) in view, only when the overlay is on.
    const loadSupport = async () => {
      if (!showSupportRef.current || m.getZoom() < LOAD_ZOOM) {
        m.getSource('support')?.setData({ type: 'FeatureCollection', features: [] });
        return;
      }
      const b = m.getBounds();
      const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(',');
      try {
        const { data } = await api.get('/api/poles/support-requests', { params: { bbox, days: 90 } });
        m.getSource('support')?.setData({
          type: 'FeatureCollection',
          features: (data.requests || [])
            .filter((r) => r.longitude != null && r.latitude != null)
            .map((r) => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [r.longitude, r.latitude] },
              properties: { ...r, approved: /approv/i.test(r.decision || '') ? 1 : 0 },
            })),
        });
      } catch { /* leave what's there */ }
    };
    loadSupportRef.current = loadSupport;
    const debounced = () => { clearTimeout(timer); timer = setTimeout(() => { load(); loadHouses(); loadProperties(); loadSupport(); }, 250); };

    // Belt-and-braces: if the container sized after the map initialised, make
    // MapLibre pick up the real dimensions.
    m.on('load', () => m.resize());
    setTimeout(() => map.current && map.current.resize(), 300);

    // Progress bar for tile loading. `dataloading` fires per tile requested,
    // `data` with a tile fires per tile arrived, and `idle` means everything in
    // view has painted — so completed/requested is an honest "how far".
    const paint = () => setProgress(Math.min(96, Math.round((tilesDone.current / Math.max(tilesReq.current, 1)) * 100)));
    m.on('dataloading', (e) => { if (e.tile || e.dataType === 'source') { tilesReq.current += 1; setLoading(true); paint(); } });
    m.on('data', (e) => { if (e.tile) { tilesDone.current += 1; paint(); } });
    m.on('idle', () => {
      setProgress(100);
      window.setTimeout(() => {
        setLoading(false); setProgress(0); tilesReq.current = 0; tilesDone.current = 0;
      }, 350);
    });

    m.on('load', () => {
      // Clustered so dense areas read as a count and burst apart on zoom.
      m.addSource('poles', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterRadius: 46,
        clusterMaxZoom: 15,
      });
      m.addSource('measure', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      m.addSource('houses', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      m.addSource('properties', { type: 'geojson', data: { type: 'FeatureCollection', features: [] }, attribution: 'Contains OS data © Crown copyright and database right' });
      m.addSource('support', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      m.addSource('serving', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

      // Satellite (aerial) imagery — Esri World Imagery, free for this use with
      // attribution. Hidden until the toolbar toggle turns it on. Inserted below
      // the road/place labels so street names and the poles stay readable on top.
      m.addSource('satellite', {
        type: 'raster',
        tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
        tileSize: 256,
        maxzoom: 19,
        attribution: 'Imagery © Esri, Maxar, Earthstar Geographics',
      });
      const beforeLbl = m.getLayer('road-labels') ? 'road-labels' : undefined;
      m.addLayer({ id: 'satellite', type: 'raster', source: 'satellite', layout: { visibility: 'none' }, paint: { 'raster-opacity': 1 } }, beforeLbl);

      m.addLayer({ id: 'measure-line', type: 'line', source: 'measure', paint: { 'line-color': '#111827', 'line-width': 2, 'line-dasharray': [2, 1.5] } });

      // One dot per property (OS Open UPRN). Click one to pick that property.
      m.addLayer({
        id: 'property-dots', type: 'circle', source: 'properties', minzoom: 17,
        paint: { 'circle-radius': 4, 'circle-color': '#64748b', 'circle-opacity': 0.75, 'circle-stroke-width': 1, 'circle-stroke-color': '#ffffff' },
      });
      // House numbers on the buildings, like Odin's "53 Cowpe Road".
      m.addLayer({
        id: 'house-labels',
        type: 'symbol',
        source: 'houses',
        minzoom: 17,
        layout: {
          'text-field': ['get', 'n'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 10,
          'text-allow-overlap': false,
        },
        paint: { 'text-color': '#3f3f46', 'text-halo-color': '#ffffff', 'text-halo-width': 1.3 },
      });

      m.addLayer({
        id: 'clusters', type: 'circle', source: 'poles', filter: ['has', 'point_count'],
        paint: {
          'circle-color': BRAND,
          'circle-opacity': 0.85,
          'circle-radius': ['step', ['get', 'point_count'], 14, 25, 18, 100, 24, 500, 30],
          'circle-stroke-width': 2, 'circle-stroke-color': '#fff',
        },
      });
      m.addLayer({
        id: 'pole-dots', type: 'circle', source: 'poles', filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 4, 17, 8],
          'circle-color': ['get', 'verdictColour'],
          'circle-stroke-width': 1.2, 'circle-stroke-color': '#ffffff',
        },
      });
      // The picked address and the pole that feeds it: a solid brand line from
      // pole to house, a ring round the pole, a square on the house.
      m.addLayer({ id: 'serving-line', type: 'line', source: 'serving', filter: ['==', ['geometry-type'], 'LineString'], paint: { 'line-color': BRAND, 'line-width': 3 } });
      m.addLayer({
        id: 'serving-pole', type: 'circle', source: 'serving', filter: ['==', ['get', 'role'], 'pole'],
        paint: { 'circle-radius': 14, 'circle-color': 'rgba(0,0,0,0)', 'circle-stroke-width': 3, 'circle-stroke-color': BRAND },
      });
      m.addLayer({
        id: 'serving-house', type: 'circle', source: 'serving', filter: ['==', ['get', 'role'], 'house'],
        paint: { 'circle-radius': 6, 'circle-color': BRAND, 'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff' },
      });
      // CBT badge: a small purple "C" beside every pole that carries a closure.
      const makeBadge = () => {
        const scale = 2; const w = 16; const h = 16;
        const cv = document.createElement('canvas');
        cv.width = w * scale; cv.height = h * scale;
        const ctx = cv.getContext('2d');
        ctx.scale(scale, scale);
        ctx.fillStyle = BRAND; ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1.5;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(1, 1, w - 2, h - 2, 3); else ctx.rect(1, 1, w - 2, h - 2);
        ctx.fill(); ctx.stroke();
        ctx.fillStyle = '#ffffff'; ctx.font = 'bold 10px system-ui, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('C', w / 2, h / 2 + 0.5);
        const d = ctx.getImageData(0, 0, w * scale, h * scale);
        return { width: w * scale, height: h * scale, data: new Uint8Array(d.data) };
      };
      if (!m.hasImage('cbt-badge')) m.addImage('cbt-badge', makeBadge(), { pixelRatio: 2 });
      m.addLayer({
        id: 'pole-cbt', type: 'symbol', source: 'poles', minzoom: 14,
        filter: ['all', ['!', ['has', 'point_count']], ['to-boolean', ['get', 'cbt']]],
        layout: {
          'icon-image': 'cbt-badge',
          'icon-size': ['interpolate', ['linear'], ['zoom'], 14, 0.8, 17, 1.1],
          'icon-offset': [10, -10],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
      // Pole DP/pole number under the dot, zoomed in — like Odin's "L4 - 740419".
      m.addLayer({
        id: 'pole-labels',
        type: 'symbol',
        source: 'poles',
        filter: ['!', ['has', 'point_count']],
        minzoom: 16,
        layout: {
          'text-field': ['concat', ['get', 'dp'], '/', ['get', 'pole']],
          'text-font': ['Noto Sans Regular'],
          'text-size': 10,
          'text-offset': [0, 1.1],
          'text-anchor': 'top',
        },
        paint: { 'text-color': '#111827', 'text-halo-color': '#ffffff', 'text-halo-width': 1.3 },
      });
      // Support requests overlay — TEARDROP PINS (not dots), so they read as a
      // distinct thing floating above the round pole dots. Colour = outcome.
      // Off until the toolbar toggle turns it on.
      const makePin = (color) => {
        const scale = 2; const w = 30; const h = 42; const cx = 15; const cyc = 14; const r = 11;
        const cv = document.createElement('canvas');
        cv.width = w * scale; cv.height = h * scale;
        const ctx = cv.getContext('2d');
        ctx.scale(scale, scale);
        ctx.fillStyle = color; ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 2.5;
        ctx.lineJoin = 'round';
        ctx.beginPath();          // teardrop: head circle + point to the tip
        ctx.moveTo(cx, h - 1);
        ctx.lineTo(cx - 8, cyc + 7);
        ctx.arc(cx, cyc, r, Math.PI * 0.8, Math.PI * 0.2, false);
        ctx.closePath();
        ctx.fill(); ctx.stroke();
        ctx.fillStyle = '#ffffff';
        ctx.beginPath(); ctx.arc(cx, cyc, 3.5, 0, Math.PI * 2); ctx.fill();
        const d = ctx.getImageData(0, 0, w * scale, h * scale);
        return { width: w * scale, height: h * scale, data: new Uint8Array(d.data) };
      };
      if (!m.hasImage('sr-pin-amber')) m.addImage('sr-pin-amber', makePin('#f59e0b'), { pixelRatio: 2 });
      if (!m.hasImage('sr-pin-red')) m.addImage('sr-pin-red', makePin('#dc2626'), { pixelRatio: 2 });
      m.addLayer({
        id: 'support-dots', type: 'symbol', source: 'support',
        layout: {
          'icon-image': ['case', ['==', ['get', 'approved'], 1], 'sr-pin-amber', 'sr-pin-red'],
          'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 0.55, 16, 0.9],
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
        },
      });
      load();
      loadHouses();
      loadProperties();
      loadSupport();
    });

    m.on('moveend', debounced);

    // Cluster → zoom to expand it.
    m.on('click', 'clusters', (e) => {
      const f = m.queryRenderedFeatures(e.point, { layers: ['clusters'] })[0];
      m.getSource('poles').getClusterExpansionZoom(f.properties.cluster_id).then((z) => {
        m.easeTo({ center: f.geometry.coordinates, zoom: z, duration: 400 });
      }).catch(() => {});
    });
    // No glyph server, so the count shows on hover as an HTML popup rather than
    // as map text.
    let clusterHover = null;
    m.on('mouseenter', 'clusters', (e) => {
      m.getCanvas().style.cursor = 'pointer';
      const f = e.features && e.features[0];
      if (!f) return;
      clusterHover = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 10 })
        .setLngLat(f.geometry.coordinates)
        .setHTML(`<div style="font:600 12px system-ui,sans-serif">${f.properties.point_count} poles</div>`)
        .addTo(m);
    });
    m.on('mouseleave', 'clusters', () => {
      m.getCanvas().style.cursor = '';
      if (clusterHover) { clusterHover.remove(); clusterHover = null; }
    });

    // Pole → full breakdown panel + arm the distance measure.
    m.on('mouseenter', 'pole-dots', () => { m.getCanvas().style.cursor = 'pointer'; });
    m.on('mouseleave', 'pole-dots', () => { m.getCanvas().style.cursor = ''; });
    m.on('click', 'pole-dots', (e) => {
      const f = e.features && e.features[0];
      if (!f) return;
      // MapLibre serialises array/object feature properties to strings through
      // the cluster worker, so `reasons` (an array) arrives as JSON text and the
      // panel's .map() would throw — parse it back, and coerce the booleans the
      // cluster worker may have stringified too.
      const p = { ...f.properties };
      if (typeof p.reasons === 'string') {
        try { p.reasons = JSON.parse(p.reasons); } catch { p.reasons = p.reasons ? [p.reasons] : []; }
      }
      if (!Array.isArray(p.reasons)) p.reasons = [];
      if (typeof p.override === 'string') {
        try { p.override = JSON.parse(p.override); } catch { p.override = null; }
      }
      p.out_of_test = p.out_of_test === true || p.out_of_test === 'true';
      p.ok_pole = p.ok_pole === true || p.ok_pole === 'true';
      setSelected(p);
      setOvrForm(null); setOvrError('');
      clearMeasure();
      recordEvidence('pole', {
        exchange: p.exchange, dp: p.dp, pole: p.pole, grid_ref: p.grid_ref, verdict: p.verdict,
        result: p.result_description || p.result, result_code: p.result || null, out_of_test: p.out_of_test, inspected: p.inspected, cbt: p.cbt || null,
      });
      // Pole-top height above ground ≈ length less the buried portion (~1.7m),
      // used as the top of the drop-wire line for the tree-in-path check.
      const len = Number(p.length_m);
      const poleTopM = Number.isFinite(len) && len > 0 ? Math.max(3, len - 1.7) : null;
      measureFrom.current = { coords: f.geometry.coordinates.slice(), label: `DP ${p.dp}/${p.pole}`, poleTopM };
      setHint('Now click the house or point to measure the distance and check for trees in the drop path.');
    });

    // Support request → details popup (its own layer, so it doesn't disturb the
    // pole panel or the distance measure).
    m.on('mouseenter', 'support-dots', () => { m.getCanvas().style.cursor = 'pointer'; });
    m.on('mouseleave', 'support-dots', () => { m.getCanvas().style.cursor = ''; });
    m.on('click', 'support-dots', (e) => {
      const f = e.features && e.features[0];
      if (!f) return;
      const p = f.properties;
      const rows = [
        ['Type', p.type], ['MEWP', p.mewp], ['Specialist', p.specialist], ['Supervisor', p.supervisor],
        ['Engineer', p.engineer], ['Region', p.region], ['CBT', p.cbt],
        ['Decision', p.decision], ['Requested', p.requested_at], ['Approved', p.approved_at],
        ['Comments', p.comments],
      ].filter(([, v]) => v && String(v).trim() && String(v) !== 'null');
      const dateStr = p.date ? new Date(p.date).toLocaleDateString('en-GB') : '';
      const html = `<div style="font:400 12px system-ui,sans-serif;max-width:260px">
        <div style="font-weight:700;margin-bottom:2px">${escapeHtml(p.address || 'Support request')}</div>
        <div style="color:#6b7280;margin-bottom:6px">${escapeHtml([p.contract, dateStr].filter(Boolean).join(' · '))}</div>
        ${rows.map(([k, v]) => `<div><span style="color:#6b7280">${k}:</span> ${escapeHtml(String(v))}</div>`).join('')}
        ${p.geo_source === 'postcode' ? '<div style="color:#9ca3af;margin-top:6px;font-size:11px">Placed at postcode centre (no exact match).</div>' : ''}
        <div style="margin-top:8px">
          <a href="https://www.google.com/maps/search/?api=1&query=${f.geometry.coordinates[1]},${f.geometry.coordinates[0]}" target="_blank" rel="noopener noreferrer" style="color:#2563eb;text-decoration:none;font-weight:600">View on Google Maps ↗</a>
        </div>
      </div>`;
      new maplibregl.Popup({ offset: 12, maxWidth: '300px' }).setLngLat(f.geometry.coordinates.slice()).setHTML(html).addTo(m);
    });

    // A property dot, when nothing is being measured: pick that property, named
    // from the nearest known address (or its UPRN), ready for a survey.
    m.on('mouseenter', 'property-dots', () => { if (!measureFrom.current) m.getCanvas().style.cursor = 'pointer'; });
    m.on('mouseleave', 'property-dots', () => { m.getCanvas().style.cursor = ''; });
    m.on('click', 'property-dots', (e) => {
      if (measureFrom.current) return;
      if (m.queryRenderedFeatures(e.point, { layers: ['pole-dots', 'pole-cbt', 'support-dots'] }).length) return;
      const f = e.features && e.features[0];
      if (!f) return;
      api.get(`/api/poles/property/${f.properties.uprn}`).then(({ data }) => {
        setAddr(data.label);
        setPlace({ id: data.premise_id, label: data.label, lat: data.lat, lon: data.lon, postcode: data.postcode, uprn: data.uprn });
      }).catch(() => {});
    });

    // Second click (not a pole/cluster/support) measures.
    m.on('click', (e) => {
      if (!measureFrom.current) return;
      if (m.queryRenderedFeatures(e.point, { layers: ['pole-dots', 'clusters', 'support-dots', 'pole-cbt'] }).length) return;
      // Snap to the property dot under the click, if there is one, so the
      // drop is measured to the property's own point.
      const dot = m.queryRenderedFeatures([[e.point.x - 6, e.point.y - 6], [e.point.x + 6, e.point.y + 6]], { layers: ['property-dots'] })[0];
      const to = dot ? dot.geometry.coordinates.slice() : [e.lngLat.lng, e.lngLat.lat];
      const from = measureFrom.current.coords;
      const label = measureFrom.current.label;
      const metres = metresBetween(from, to);
      m.getSource('measure').setData({ type: 'FeatureCollection', features: [{ type: 'Feature', geometry: { type: 'LineString', coordinates: [from, to] }, properties: {} }] });
      m.setPaintProperty('measure-line', 'line-color', '#6b7280');
      if (houseMarker.current) houseMarker.current.remove();
      if (treeMarker.current) { treeMarker.current.remove(); treeMarker.current = null; }
      const el = document.createElement('div');
      el.style.cssText = 'width:14px;height:14px;border-radius:2px;background:#111827;border:2px solid #fff;box-shadow:0 0 0 1px #999';
      houseMarker.current = new maplibregl.Marker({ element: el }).setLngLat(to).addTo(m);
      if (distPopup.current) distPopup.current.remove();
      const mid = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2];
      const baseHtml = (extra) => `<div style="font:600 13px system-ui,sans-serif">${metres} m<br><span style="font-weight:400;color:#6b7280;font-size:11px">from ${escapeHtml(label)} · straight line</span>${extra || '<br><span style="font-weight:400;color:#9ca3af;font-size:11px">checking trees…</span>'}</div>`;
      distPopup.current = new maplibregl.Popup({ closeButton: false, offset: 8 }).setLngLat(mid).setHTML(baseHtml()).addTo(m);
      setHint(`${metres} m from ${label} — click another pole to measure again.`);
      // Line-of-sight tree check: sample canopy along the pole→point path, against
      // the drop-wire line (pole top → property attach) when we know the pole height.
      const poleTop = measureFrom.current.poleTopM;
      api.get('/api/poles/drop-path', { params: { from: `${from[0]},${from[1]}`, to: `${to[0]},${to[1]}`, ...(poleTop ? { poleTop } : {}) } })
        .then(({ data }) => {
          recordEvidence('drop_path', {
            from: label, metres, status: data.assessment?.status || null, headline: data.assessment?.headline || null,
            recommendation: data.assessment?.recommendation || null, source: data.source || null,
          });
          if (!distPopup.current) return;
          const as = data.assessment || {};
          const clear = as.status === 'clear';
          const heavy = /heav/i.test(as.headline || '');
          const colour = clear ? '#16a34a' : heavy ? '#dc2626' : '#d97706';
          m.setPaintProperty('measure-line', 'line-color', colour);
          if (data.obstruction) {
            const te = document.createElement('div');
            te.style.cssText = 'font-size:18px;line-height:1;filter:drop-shadow(0 1px 1px rgba(0,0,0,.4))';
            te.textContent = '🌳';
            treeMarker.current = new maplibregl.Marker({ element: te, anchor: 'bottom' }).setLngLat([data.obstruction.lng, data.obstruction.lat]).addTo(m);
          }
          const prop = data.property
            ? `<div style="margin-top:4px;color:#6b7280;font-size:11px">Property ~${data.property.roof_m} m high (LIDAR) · wire attaches ~${data.property.attach_m} m</div>`
            : '';
          const src = `<div style="margin-top:3px;color:#9ca3af;font-size:10px">trees checked from ${data.source === 'lidar' ? '1 m LIDAR surface' : 'satellite canopy (LIDAR unavailable)'}</div>`;
          distPopup.current.setHTML(`<div style="font:400 12px system-ui,sans-serif;max-width:240px">
            <div style="font-weight:700;font-size:13px">${metres} m from ${escapeHtml(label)}</div>
            <div style="margin-top:5px;color:${colour};font-weight:700">${clear ? '✓ ' : '⚠ '}${escapeHtml(as.headline || '')}</div>
            <div style="margin-top:2px;color:#4b5563">${escapeHtml(as.detail || '')}</div>
            ${prop}
            <div style="margin-top:5px;color:#111827;font-weight:600">→ ${escapeHtml(as.recommendation || '')}</div>
            ${src}
          </div>`);
        })
        .catch(() => {
          m.setPaintProperty('measure-line', 'line-color', '#111827');
          distPopup.current?.setHTML(baseHtml('<br><span style="color:#9ca3af;font-size:11px">tree check unavailable</span>'));
        });
    });

    return () => { clearTimeout(timer); m.remove(); map.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jumpPostcode = async (e) => {
    e.preventDefault();
    if (!pc.trim()) return;
    try {
      const { data } = await api.get('/api/poles/geocode', { params: { postcode: pc.trim() } });
      map.current?.flyTo({ center: [data.lon, data.lat], zoom: 16, duration: 800 });
    } catch (err) { setStatus('Postcode not found.'); }
  };

  const jumpPole = async (e) => {
    e.preventDefault();
    const { exchange, dp, pole } = poleQ;
    if (!exchange || !dp || !pole) return;
    try {
      const { data } = await api.get('/api/poles/by-pole', { params: { exchange, dp, pole } });
      const hit = (data.poles || [])[0];
      if (!hit || hit.latitude == null) { setStatus('Pole found but has no location.'); return; }
      map.current?.flyTo({ center: [hit.longitude, hit.latitude], zoom: 17, duration: 800 });
      setSelected({ ...hit, verdictColour: VERDICT_COLOUR[hit.verdict] });
      measureFrom.current = { coords: [hit.longitude, hit.latitude], label: `DP ${hit.dp}/${hit.pole}` };
      setHint('Click the house or point to measure the distance from this pole.');
    } catch (err) { setStatus('No pole matched those details.'); }
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col p-4 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3 pb-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Pole Map</h1>
          <p className="text-sm text-gray-500">
            Every Openreach pole in England, coloured by climb status. Search to fly there; click a pole
            for its record, then click a point to measure the distance.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-gray-300" style={{ width: 'fit-content' }}>
            {[['address', 'Address'], ['postcode', 'Postcode'], ['pole', 'Pole no.']].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setMode(k)}
                className={`px-3 py-1.5 text-sm font-medium ${mode === k ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                style={mode === k ? { background: BRAND } : undefined}
              >{label}</button>
            ))}
          </div>
          {mode === 'address' ? (
            <div className="relative">
              <input
                value={addr}
                onChange={(e) => { setAddr(e.target.value); setAddrOpen(true); }}
                onFocus={() => setAddrOpen(true)}
                onBlur={() => window.setTimeout(() => setAddrOpen(false), 150)}
                placeholder="Start typing an address…"
                className="w-80 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                autoComplete="off"
              />
              {addrOpen && addr.trim().length >= 3 ? (
                <ul className="absolute z-20 mt-1 max-h-72 w-80 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                  {addrList.map((a) => (
                    <li key={a.id}>
                      <button
                        type="button"
                        onMouseDown={(ev) => {
                          ev.preventDefault();
                          setAddr(a.label); setAddrOpen(false);
                          setPlace({ id: a.id, label: a.label, lat: a.lat, lon: a.lon, postcode: a.postcode || null });
                          map.current?.flyTo({ center: [a.lon, a.lat], zoom: 17, duration: 800 });
                        }}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-brand-light"
                      >
                        <span className="truncate">{a.label}</span>
                        {a.kind === 'road' || a.kind === 'postcode' ? <span className="ml-auto shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">{a.kind}</span> : null}
                      </button>
                    </li>
                  ))}
                  {!addrList.length ? <li className="px-3 py-2 text-sm text-gray-400">No match (address data may not be loaded yet).</li> : null}
                </ul>
              ) : null}
            </div>
          ) : mode === 'postcode' ? (
            <form onSubmit={jumpPostcode} className="flex items-center gap-2">
              <input value={pc} onChange={(e) => setPc(e.target.value)} placeholder="e.g. SK8 4AX"
                className="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
              <button type="submit" className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold text-white" style={{ background: BRAND }}>
                <MagnifyingGlassIcon className="h-4 w-4" /> Go
              </button>
            </form>
          ) : (
            <form onSubmit={jumpPole} className="flex items-center gap-2">
              <input value={poleQ.exchange} onChange={(e) => setPoleQ({ ...poleQ, exchange: e.target.value.toUpperCase() })} placeholder="Exch" className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-sm" />
              <input value={poleQ.dp} onChange={(e) => setPoleQ({ ...poleQ, dp: e.target.value })} placeholder="DP" className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-sm" />
              <input value={poleQ.pole} onChange={(e) => setPoleQ({ ...poleQ, pole: e.target.value })} placeholder="Pole" className="w-24 rounded-md border border-gray-300 px-2 py-1.5 text-sm" />
              <button type="submit" className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold text-white" style={{ background: BRAND }}>
                <MagnifyingGlassIcon className="h-4 w-4" /> Go
              </button>
            </form>
          )}
          <button
            type="button"
            onClick={() => {
              const next = !showSupport;
              setShowSupport(next);
              showSupportRef.current = next;
              loadSupportRef.current();
            }}
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium ${showSupport ? 'border-transparent text-white' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}`}
            style={showSupport ? { background: '#f59e0b' } : undefined}
            title="Show recent MEWP / support requests (last 90 days) on the map"
          >
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: showSupport ? '#fff' : '#f59e0b' }} />
            Support requests
          </button>
          <button
            type="button"
            onClick={() => {
              const next = !showSatellite;
              setShowSatellite(next);
              const m = map.current;
              if (!m) return;
              if (m.getLayer('satellite')) m.setLayoutProperty('satellite', 'visibility', next ? 'visible' : 'none');
              // Keep labels legible: white text over imagery, dark over the map.
              ['road-labels', 'place-labels'].forEach((id) => {
                if (m.getLayer(id)) {
                  m.setPaintProperty(id, 'text-color', next ? '#ffffff' : (id === 'road-labels' ? '#5b5560' : '#4a4551'));
                  m.setPaintProperty(id, 'text-halo-color', next ? 'rgba(0,0,0,0.75)' : '#ffffff');
                }
              });
            }}
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium ${showSatellite ? 'border-transparent text-white' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}`}
            style={showSatellite ? { background: '#166534' } : undefined}
            title="Satellite imagery — see trees and obstructions at the pole"
          >
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: showSatellite ? '#fff' : '#166534' }} />
            Satellite
          </button>
        </div>
      </div>

      {/* Survey bar (TKT-00042): start once an address is picked; while open,
          every pole opened and drop-path checked is recorded against it. */}
      <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
        <ClipboardDocumentCheckIcon className="h-5 w-5 shrink-0" style={{ color: BRAND }} />
        {blocked && !survey ? (
          // One survey at a time: the open one is in another tab. Never cancel it silently.
          <>
            <span className="min-w-0 flex-1 text-amber-800">
              You are still surveying <span className="font-semibold">{blocked.address}</span>
              {blocked.started_at ? ` (open since ${new Date(blocked.started_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })})` : ''}.
              {' '}Only one survey can be open at a time.
            </span>
            <button type="button" onClick={leaveForOpen} className="rounded-md px-3 py-1.5 text-sm font-semibold text-white" style={{ background: BRAND }}>
              Go back to that survey
            </button>
            {place ? (
              <button type="button" onClick={() => startSurvey(blocked)} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                Cancel it and survey this address
              </button>
            ) : null}
          </>
        ) : survey ? (
          <>
            <span className="min-w-0 flex-1 truncate">
              Surveying <span className="font-semibold text-gray-900">{survey.address}</span>
              <span className="ml-2 inline-flex items-center gap-1 text-gray-500"><ClockIcon className="h-4 w-4" />{elapsed(survey.started_at)}</span>
            </span>
            <button type="button" onClick={openFinish} className="rounded-md px-3 py-1.5 text-sm font-semibold text-white" style={{ background: BRAND }}>
              Finish survey
            </button>
            <button type="button" onClick={cancelSurvey} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          </>
        ) : place ? (
          <>
            <span className="min-w-0 flex-1 truncate text-gray-600">
              Ready to survey <span className="font-semibold text-gray-900">{place.label}</span>
            </span>
            <button type="button" onClick={() => startSurvey()} className="rounded-md px-3 py-1.5 text-sm font-semibold text-white" style={{ background: BRAND }}>
              Start survey
            </button>
          </>
        ) : (
          <span className="flex-1 text-gray-500">Search an address to start a survey.</span>
        )}
        {surveyNote ? <span className="w-full text-xs font-medium text-amber-700">{surveyNote}</span> : null}
        {ccJob ? (
          <span className="w-full text-xs text-gray-600">
            From Climb Check: <span className="font-mono">{ccJob.wo}</span>
            {ccJob.l4_closure ? ` · CBT L4 ${ccJob.l4_closure}` : ''}
            {ccJob.summary ? <span className="text-gray-500"> · {ccJob.summary}</span> : null}
            {ccLinked && !survey ? <span className="font-medium text-gray-800"> · the outcome you save goes back onto this job</span> : null}
            {survey && survey.climb_check_job_id === ccJob.id ? <span className="font-medium text-gray-800"> · this survey updates the job</span> : null}
            {ccNote && !(survey && survey.climb_check_job_id === ccJob.id) ? <span className="block font-medium text-amber-700">{ccNote}</span> : null}
          </span>
        ) : null}
        {place && serving?.premise ? (
          <span className="w-full text-xs text-gray-600">
            {serving.pole ? (
              <>
                Served by pole <span className="font-semibold text-gray-900">{serving.pole.exchange} DP {serving.pole.dp}/{serving.pole.pole}</span>
                {serving.premise.closure ? ` · CBT ${serving.premise.closure}` : ''}
                {serving.premise.distance_m != null ? ` · ~${serving.premise.distance_m} m drop` : ''}
                {serving.premise.network ? ` · Region ${serving.premise.network}` : ''}
                <button type="button" onClick={openServingPole} className="ml-2 font-semibold text-brand hover:text-brand-dark hover:underline">Open pole</button>
              </>
            ) : serving.premise.overhead ? (
              <>Overhead, but the feeding pole is not in our pole register{serving.premise.closure ? ` · CBT ${serving.premise.closure}` : ''}.</>
            ) : (
              <>Underground — fed from closure {serving.premise.closure || 'unknown'}{serving.premise.closure_model ? ` (${serving.premise.closure_model})` : ''}, no pole.</>
            )}
          </span>
        ) : place && serving && !serving.premise ? (
          <span className="w-full text-xs text-gray-500">Not in the partner's build data — no serving pole on record for this address.</span>
        ) : null}
        {surveyMeta.can_view_log ? (
          <Link to="/pole-survey/log" className="ml-auto text-sm font-medium text-brand hover:text-brand-dark hover:underline">Survey log</Link>
        ) : null}
        {surveyError && !finishing ? <span className="w-full text-xs text-rose-700">{surveyError}</span> : null}
      </div>

      <div className="relative flex-1">
        {/* h-full, NOT absolute inset-0: MapLibre forces position:relative on its
            own container, which defeats absolute positioning and collapses the
            height to the border. The parent flex-1 has a definite height, so
            height:100% fills it. */}
        <div ref={holder} className="h-full w-full overflow-hidden rounded-lg border border-gray-200" />

        {/* Tile-load progress — a thin bar along the top of the map. */}
        {loading ? (
          <div className="pointer-events-none absolute left-0 right-0 top-0 z-30 h-1 overflow-hidden rounded-t-lg bg-black/5">
            <div className="h-full transition-[width] duration-200 ease-out" style={{ width: `${progress}%`, background: BRAND }} />
          </div>
        ) : null}

        <div className="pointer-events-none absolute left-2 top-2 flex flex-wrap items-center gap-2">
          <span className="pointer-events-auto rounded-md bg-white/95 px-2.5 py-1 text-xs text-gray-700 shadow-sm ring-1 ring-gray-200">{status}</span>
          {houseNote ? <span className="pointer-events-auto rounded-md bg-amber-50/95 px-2.5 py-1 text-xs text-amber-800 shadow-sm ring-1 ring-amber-200">{houseNote}</span> : null}
          {hint ? <span className="pointer-events-auto rounded-md bg-white/95 px-2.5 py-1 text-xs text-gray-700 shadow-sm ring-1 ring-gray-200">{hint}</span> : null}
          {hint ? <button type="button" onClick={clearMeasure} className="pointer-events-auto rounded-md bg-white/95 px-2 py-1 text-xs font-medium text-brand shadow-sm ring-1 ring-gray-200">Clear</button> : null}
        </div>

        <div className="pointer-events-none absolute bottom-2 left-2 flex flex-wrap gap-3 rounded-md bg-white/95 px-2.5 py-1.5 text-xs text-gray-600 shadow-sm ring-1 ring-gray-200">
          {[['climb', 'Climbable'], ['caution', 'Assess'], ['hoist', 'Hoist']].map(([k, label]) => (
            <span key={k} className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-full" style={{ background: VERDICT_COLOUR[k], border: '1.5px solid #fff', boxShadow: '0 0 0 1px #ccc' }} />
              {label}
            </span>
          ))}
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: '#64748b', border: '1px solid #fff', boxShadow: '0 0 0 1px #ccc' }} />
            Property
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-[3px] text-[9px] font-bold leading-none text-white" style={{ background: BRAND }}>C</span>
            CBT on pole
          </span>
          {showSupport ? (
            <>
              <span className="mx-0.5 self-stretch border-l border-gray-200" />
              <span className="flex items-center gap-1.5"><PinGlyph color="#f59e0b" /> Request · approved</span>
              <span className="flex items-center gap-1.5"><PinGlyph color="#dc2626" /> Request · pending</span>
            </>
          ) : null}
        </div>

        {/* Full breakdown of the clicked pole. */}
        {selected ? (
          <div className="absolute right-2 top-2 w-72 rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-bold text-gray-900">DP {selected.dp}/{selected.pole}</p>
                <p className="text-xs text-gray-500">{selected.exchange} · {selected.address || selected.grid_ref}</p>
              </div>
              <button type="button" onClick={() => { setSelected(null); clearMeasure(); }} aria-label="Close" className="-mr-1 -mt-1 shrink-0 rounded-md border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"><XMarkIcon className="h-5 w-5" /></button>
            </div>
            <div className="mt-2 rounded-md px-2.5 py-1.5 text-sm font-semibold" style={{ background: `${VERDICT_COLOUR[selected.verdict]}22`, color: VERDICT_COLOUR[selected.verdict] }}>
              {verdictLabel(selected.verdict)}
              {selected.override && !selected.override.stale ? <span className="ml-1 text-[11px] font-medium opacity-80">· manual override</span> : null}
            </div>
            {selected.override && !selected.override.stale ? (
              <div className="mt-2 rounded-md border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-xs text-violet-900">
                <p className="font-semibold">Set by {selected.override.set_by || 'a pole survey manager'}{selected.override.set_at ? ` on ${ukDate(selected.override.set_at)}` : ''}</p>
                <p>{selected.override.reason_label}{selected.override.note ? `: ${selected.override.note}` : ''}</p>
                <p className="mt-0.5 text-[11px] text-violet-700">Applies until Openreach&apos;s record for this pole changes.</p>
              </div>
            ) : null}
            {surveyMeta.can_view_log && !ovrForm ? (
              <div className="mt-2 flex gap-1.5">
                <button
                  type="button"
                  onClick={() => { setOvrError(''); setOvrForm({ verdict: selected.verdict === 'climb' ? 'hoist' : 'climb', reason: 'new_pole', note: '' }); }}
                  className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  {selected.override && !selected.override.stale ? 'Change override' : 'Override this call'}
                </button>
                {selected.override && !selected.override.stale ? (
                  <button
                    type="button"
                    disabled={ovrBusy}
                    onClick={async () => {
                      setOvrBusy(true); setOvrError('');
                      try {
                        await api.post(`/api/poles/overrides/${selected.override.id}/clear`);
                        setSelected((s) => ({ ...s, override: null, reasons: (s.reasons || []).filter((r) => !r.startsWith('Manual override')) }));
                        setHint('Override cleared. The pole goes back to the register call when the map next loads it.');
                      } catch (err) {
                        setOvrError(err?.response?.data?.error || 'Could not clear the override.');
                      } finally { setOvrBusy(false); }
                    }}
                    className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >Clear</button>
                ) : null}
              </div>
            ) : null}
            {ovrForm ? (
              <div className="mt-2 space-y-1.5 rounded-md border border-gray-200 bg-gray-50 p-2">
                <label className="block text-[11px] font-medium uppercase tracking-wide text-gray-500" htmlFor="ovr-verdict">Call</label>
                <select id="ovr-verdict" value={ovrForm.verdict} onChange={(e) => setOvrForm({ ...ovrForm, verdict: e.target.value })} className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm">
                  <option value="climb">Climbable</option>
                  <option value="caution">Assess on site</option>
                  <option value="hoist">Hoist required</option>
                </select>
                <label className="block text-[11px] font-medium uppercase tracking-wide text-gray-500" htmlFor="ovr-reason">Reason</label>
                <select id="ovr-reason" value={ovrForm.reason} onChange={(e) => setOvrForm({ ...ovrForm, reason: e.target.value })} className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm">
                  {Object.entries(OVERRIDE_REASONS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
                <textarea
                  id="ovr-note" rows={2} maxLength={500} value={ovrForm.note}
                  onChange={(e) => setOvrForm({ ...ovrForm, note: e.target.value })}
                  placeholder={ovrForm.reason === 'other' ? 'Say why (required)' : 'Note, e.g. D-plate test date (optional)'}
                  className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                />
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    disabled={ovrBusy}
                    onClick={async () => {
                      setOvrBusy(true); setOvrError('');
                      try {
                        const { data } = await api.post('/api/poles/overrides', {
                          exchange: selected.exchange, dp: selected.dp, pole: selected.pole, grid_ref: selected.grid_ref, ...ovrForm,
                        });
                        const o = data.override;
                        const line = `Manual override by ${o.set_by || 'you'}: ${o.reason_label}${o.note ? ` (${o.note})` : ''}`;
                        setSelected((s) => ({ ...s, verdict: o.verdict, override: o, reasons: [line, ...(s.reasons || []).filter((r) => !r.startsWith('Manual override'))] }));
                        setOvrForm(null);
                        setHint('Override saved. Pin colours update when the map next loads this area.');
                      } catch (err) {
                        setOvrError(err?.response?.data?.error || 'Could not save the override.');
                      } finally { setOvrBusy(false); }
                    }}
                    className="flex-1 rounded-md px-2 py-1 text-xs font-semibold text-white disabled:opacity-50"
                    style={{ background: BRAND }}
                  >{ovrBusy ? 'Saving…' : 'Save override'}</button>
                  <button type="button" onClick={() => { setOvrForm(null); setOvrError(''); }} className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                </div>
              </div>
            ) : null}
            {ovrError ? <p className="mt-1 text-xs font-medium text-red-600">{ovrError}</p> : null}
            <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
              <Field label="CBT / closure">{selected.cbt ? `${selected.cbt}${selected.closure_model ? ` · ${selected.closure_model}` : ''}${selected.cbt_premises ? ` · ${selected.cbt_premises} prem.` : ''}${selected.network ? ` · Region ${selected.network}` : ''}` : '—'}</Field>
              <Field label="Trees (canopy)">{selected.canopy_m != null && Number(selected.canopy_m) >= 2 ? `${Number(selected.canopy_m)} m 🌳 within ~15m` : 'None nearby'}</Field>
              <Field label="Result">{selected.result_description || selected.result}</Field>
              <Field label="Inspected">{ukDate(selected.inspected) || 'Not recorded'}{selected.inspection_age_years != null ? ` · ${selected.inspection_age_years}y` : ''}</Field>
              <Field label="Out of test">{selected.out_of_test ? 'Yes' : 'No'}</Field>
              <Field label="Type / length">{[selected.pole_type, selected.length_m && `${selected.length_m}m`].filter(Boolean).join(' · ')}</Field>
              <Field label="Class">{selected.pole_class}</Field>
              <Field label="Dropwires">{selected.dropwires}</Field>
              <Field label="Cables">{selected.cables}</Field>
            </dl>
            {selected.reasons?.length ? (
              <ul className="mt-2 space-y-0.5 text-xs text-gray-600">{selected.reasons.map((r) => <li key={r}>· {r}</li>)}</ul>
            ) : null}
            {servedBy && servedBy.length ? (
              <div className="mt-3 border-t border-gray-100 pt-2">
                <p className="text-xs font-semibold text-gray-700">Feeds {servedBy.length} premise{servedBy.length === 1 ? '' : 's'}</p>
                <ul className="mt-1 max-h-28 space-y-0.5 overflow-y-auto text-xs text-gray-600">
                  {servedBy.map((h) => (
                    <li key={h.id} className="truncate" title={h.address}>
                      {String(h.address).split(',')[0]}{h.distance_m != null ? <span className="text-gray-400"> · {h.distance_m} m</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : servedBy ? <p className="mt-3 border-t border-gray-100 pt-2 text-xs text-gray-400">No premises recorded as fed from this pole.</p> : null}
            {selected.latitude != null && selected.longitude != null ? (
              <div className="mt-3 grid grid-cols-3 gap-1.5">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${selected.latitude},${selected.longitude}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 px-1.5 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                >Maps</a>
                <a
                  href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${selected.latitude},${selected.longitude}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 px-1.5 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                  title="Nearest Street View panorama — can be black for poles far from a road"
                >Street View</a>
                <a
                  href={`https://www.google.com/maps/@?api=1&map_action=map&center=${selected.latitude},${selected.longitude}&zoom=20&basemap=satellite`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 px-1.5 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                  title="Top-down satellite view — best for seeing trees/obstructions around the pole"
                >Aerial</a>
              </div>
            ) : null}
            <p className="mt-2 text-[11px] text-gray-400">Click a house on the map to measure the distance from this pole.</p>
          </div>
        ) : null}
      </div>

      {finishing && survey ? (
        <Modal
          title="Finish survey"
          subtitle={`${survey.address} · ${elapsed(survey.started_at)}`}
          onClose={() => setFinishing(false)}
          width="max-w-lg"
          footer={(
            <div className="flex items-center justify-end gap-2">
              {surveyError ? <span className="mr-auto text-xs text-rose-700">{surveyError}</span> : null}
              <button type="button" onClick={() => setFinishing(false)} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50">Back to map</button>
              <button type="button" disabled={!outcome || (outcome === 'hoist' && !mewpPicks.length)} onClick={finishSurvey} className="rounded-md px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50" style={{ background: BRAND }}>
                Save survey
              </button>
            </div>
          )}
        >
          <p className="mb-2 text-sm font-medium text-gray-700">Outcome</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {Object.entries(surveyMeta.outcomes || {}).map(([k, v]) => (
              <label key={k} className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${outcome === k ? 'border-brand bg-brand-light' : 'border-gray-300 bg-white hover:border-brand'}`}>
                <input type="radio" name="survey-outcome" checked={outcome === k} onChange={() => setOutcome(k)} className="text-brand focus:ring-brand" />
                {v}
              </label>
            ))}
          </div>
          {outcome === 'hoist' ? (
            <fieldset className="mt-4">
              <legend className="text-sm font-medium text-gray-700">
                Why is the hoist needed? <span className="text-rose-600">*</span>
                <span className="ml-1 font-normal text-gray-400">pick at least one</span>
              </legend>
              <p className="mt-0.5 text-xs text-gray-500">Ticked from the poles and drop paths you checked; change them if the evidence is wrong. Use Pole Survey when nothing more specific applies.</p>
              <div className="mt-2 grid max-h-56 grid-cols-1 gap-1 overflow-y-auto rounded-md border border-gray-200 p-2 sm:grid-cols-2">
                {(surveyMeta.mewp_reasons || []).map((r) => (
                  <label key={r} className="flex cursor-pointer items-start gap-2 rounded px-1.5 py-1 text-sm text-gray-700 hover:bg-gray-50">
                    <input type="checkbox" checked={mewpPicks.includes(r)} onChange={() => toggleMewp(r)} className="mt-0.5 rounded text-brand focus:ring-brand" />
                    {r}
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}
          <label className="mt-4 block text-sm font-medium text-gray-700">
            Notes {outcome === 'other' ? <span className="text-rose-600">*</span> : <span className="font-normal text-gray-400">(optional)</span>}
            <textarea value={surveyNotes} onChange={(e) => setSurveyNotes(e.target.value)} rows={3} className="mt-1 w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
          </label>
          <p className="mt-2 text-xs text-gray-500">
            Every pole you opened and drop path you checked during this survey is saved with it.
          </p>
        </Modal>
      ) : null}
    </div>
  );
}
